import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { filter } from 'lodash';
import InputBar from '../InputBar/InputBar';

/**
 * Configuration-driven InputBar component for different interest types
 */
const InterestInputBar = ({
  interestType,
  value,
  onChange,
  termsMold,
  keywordSuggester,
  queryDataSelected,
  interests,
  onDefineInterest,
}) => {
  const commonProps = useMemo(
    () => ({
      className: 'search grayscale test-an-interest tag-buttons-off allow-boolean',
      useTags: false,
      columns: 1,
    }),
    []
  );

  // Validation function for websites
  const validateWebsites = useCallback(
    async (texts) => {
      if (!termsMold || !termsMold.validateDomain) {
        // Fallback: Basic domain validation
        const validDomainPattern =
          /^([a-z]|[a-z][a-z]|[a-z][0-9]|[0-9][a-z]|[a-z0-9][a-z0-9_-]{1,61}[a-z0-9])\.([a-z]{2,6}|[a-z0-9-]{2,30}\.[a-z]{2,3})$/i;
        return texts.map((text) => {
          const domain = text.toLowerCase().trim();
          const isValid = validDomainPattern.test(domain);
          return {
            text: domain,
            id: isValid ? domain : null,
            class: 'term-default',
            invalid: !isValid,
            type: 'domain',
            origin: text,
            display: text,
          };
        });
      }
      try {
        const result = await termsMold.validateDomain(texts);
        return result || [];
      } catch (error) {
        return [];
      }
    },
    [termsMold]
  );

  // Validation function for phrases/searches
  const validatePhrases = useCallback(
    async (texts) => {
      if (!termsMold || !termsMold.validate) {
        // Fallback: Create basic phrase tags
        return texts.map((text, index) => ({
          text,
          id: `phrase_${text.toLowerCase().trim()}_${Date.now()}_${index}`,
          class: 'term-default',
          invalid: false,
          type: 'term',
          origin: text,
        }));
      }
      try {
        const usedClasses = queryDataSelected.filter((item) => item.class).map((item) => item.class);
        const result = await termsMold.validate(texts, usedClasses);
        return result || [];
      } catch (error) {
        return [];
      }
    },
    [termsMold, queryDataSelected]
  );

  // Validation function for categories
  const validateCategories = useCallback(
    async (texts) =>
      texts.map((text) => {
        const category = interests.find((cat) => cat.phrase.toLowerCase() === text.toLowerCase());
        if (!category) {
          return {
            text,
            id: null,
            class: 'term-default',
            invalid: true,
            type: 'term',
            origin: text,
          };
        }
        // Return the FULL category object with all its properties (needed for bar chart display)
        return {
          ...category,
          text: category.phrase,
          class: 'term-default',
          invalid: false,
          type: 'term',
          origin: category.path,
        };
      }),
    [interests]
  );

  // Suggestions function for categories
  const getCategorySuggestions = useCallback(
    async (text) => {
      const filteredCategories = filter(interests, (category) =>
        category.phrase.toLowerCase().includes(text.toLowerCase())
      );
      // Return FULL category objects with all properties (needed for bar chart display)
      return filteredCategories.map((category) => ({
        ...category,
        text: category.phrase,
        class: 'term-default',
        invalid: false,
        type: 'term',
        origin: category.path,
      }));
    },
    [interests]
  );

  // Get suggestions function based on type
  const getSuggestions = useMemo(() => {
    if (interestType === 'categories') {
      return getCategorySuggestions;
    }
    if (keywordSuggester && (interestType === 'phrases' || interestType === 'searches')) {
      return (text) => keywordSuggester.getSuggestions(text);
    }
    return undefined;
  }, [interestType, keywordSuggester, getCategorySuggestions]);

  // Type-specific configurations
  const typeConfigs = useMemo(
    () => ({
      websites: {
        placeholder: 'Test a website',
        onValidate: validateWebsites,
        placeholderInvalid: 'Invalid domain',
        disableAutocomplete: true,
        disableAddToTracking: true,
      },
      phrases: {
        placeholder: 'Test a phrase',
        onValidate: validatePhrases,
        onDefineInterest,
        disablePosts: true,
        disableBooleanLogic: false,
        programBlAllowed: true,
      },
      searches: {
        placeholder: 'Test a search term',
        onValidate: validatePhrases,
        disablePosts: true,
        programBlAllowed: false,
        disableBooleanLogic: true,
      },
      categories: {
        placeholder: 'Search a category',
        onValidate: validateCategories,
        disablePosts: true,
        programBlAllowed: false,
        disableBooleanLogic: true,
      },
    }),
    [validateWebsites, validatePhrases, validateCategories, onDefineInterest]
  );

  const typeConfig = typeConfigs[interestType];

  if (!typeConfig) {
    return null;
  }

  return (
    <InputBar {...commonProps} {...typeConfig} value={value} onChange={onChange} getSuggestions={getSuggestions} />
  );
};

InterestInputBar.propTypes = {
  interestType: PropTypes.oneOf(['websites', 'phrases', 'searches', 'categories']).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  termsMold: PropTypes.object,
  keywordSuggester: PropTypes.object,
  queryDataSelected: PropTypes.array,
  interests: PropTypes.array,
  onDefineInterest: PropTypes.func,
};

InterestInputBar.defaultProps = {
  value: [],
  queryDataSelected: [],
  interests: [],
};

export default InterestInputBar;
